/**
 * V37.56 — File d'attente Juriste.
 *
 * Page dediee : liste des documents `compliance_draft_pdf` ayant un
 * `juridique_review` en attente / decidé. Tri par anciennete (assigned_at asc).
 *
 * Roles autorises (cote backend) : juriste, admin, super_admin.
 *  - Juriste pur : voit UNIQUEMENT sa propre queue.
 *  - admin/super_admin : voit tout, peut filtrer par juriste.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ArrowLeft, RefreshCw, Scale, Clock, CheckCircle2, XCircle, RotateCcw, BarChart3 } from 'lucide-react';
// V38.09 — Dashboard juriste enrichi intégré en haut de la file d'attente
import JuristeDashboardPanel from '../components/JuristeDashboardPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  en_attente: { label: 'En attente', color: 'bg-amber-600 text-white border-amber-800 font-semibold', Icon: Clock },
  valide: { label: 'Validé', color: 'bg-emerald-700 text-white border-emerald-900 font-semibold', Icon: CheckCircle2 },
  refuse: { label: 'Refusé', color: 'bg-red-700 text-white border-red-900 font-semibold', Icon: XCircle },
  a_retravailler: { label: 'À retravailler', color: 'bg-orange-600 text-white border-orange-800 font-semibold', Icon: RotateCcw },
};

const getAuthHeader = () => {
  const t = localStorage.getItem('admin_token')
        || localStorage.getItem('token')
        || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function JuristeQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [decisionModal, setDecisionModal] = useState(null); // {item, decision}
  const [decisionComment, setDecisionComment] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [roleView, setRoleView] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (assignedToFilter.trim()) params.append('assigned_to', assignedToFilter.trim());
      params.append('limit', '100');
      const res = await axios.get(`${API}/ccmi/juriste/queue?${params.toString()}`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setRoleView(res.data?.filters?.role_view || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, assignedToFilter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Note: handler open modal inline at call sites to satisfy react-hooks linter

  const submitDecision = async () => {
    if (!decisionModal) return;
    const { item, decision } = decisionModal;
    if (['refuse', 'a_retravailler'].includes(decision) && decisionComment.trim().length < 10) {
      toast.error('Commentaire d\'au moins 10 caractères requis');
      return;
    }
    setSubmittingDecision(true);
    try {
      await axios.post(
        `${API}/ccmi/projects/${item.project_id}/documents/${item.document_id}/juriste/decision`,
        { decision, comment: decisionComment.trim() || null },
        { headers: getAuthHeader() },
      );
      toast.success(`Décision enregistrée : ${decision}`);
      setDecisionModal(null);
      setDecisionComment('');
      fetchQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement décision');
    } finally {
      setSubmittingDecision(false);
    }
  };

  const counters = items.reduce((acc, it) => {
    const s = it.juridique_review?.status || 'en_attente';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // V38.09 — Affichage du dashboard analytics enrichi (replié par défaut)
  const [showAnalytics, setShowAnalytics] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/30 to-slate-50 py-8 px-4" data-testid="juriste-queue-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 rounded-lg hover:bg-white/80 transition-colors border border-slate-200"
              title="Retour"
            >
              <ArrowLeft className="h-5 w-5 text-slate-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Scale className="h-6 w-6 text-red-700" />
                File d&apos;attente juriste
              </h1>
              <p className="text-sm text-slate-500">
                Documents CCMI en attente de validation juridique
                {roleView && <Badge className="ml-2 text-[10px] bg-slate-800 text-white border-slate-900 font-semibold">vue : {roleView}</Badge>}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchQueue} disabled={loading} data-testid="juriste-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Actualiser
          </Button>
        </div>

        {/* Compteurs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="juriste-queue-counters">
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const Icon = meta.Icon;
            return (
              <Card key={key} className={meta.color.includes('amber') ? 'border-amber-200' : ''}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-xs uppercase tracking-wide text-slate-500">{meta.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-800 mt-1" data-testid={`juriste-counter-${key}`}>
                    {counters[key] || 0}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* V38.09 — Dashboard juriste enrichi (collapsible) */}
        <Card className="border-blue-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-900">
                <BarChart3 className="h-4 w-4" />
                Mes KPIs & dossiers à risque
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAnalytics((v) => !v)}
                data-testid="juriste-toggle-analytics"
              >
                {showAnalytics ? 'Masquer' : 'Afficher le dashboard enrichi'}
              </Button>
            </div>
          </CardHeader>
          {showAnalytics && (
            <CardContent>
              <JuristeDashboardPanel />
            </CardContent>
          )}
        </Card>

        {/* Filtres */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="juriste-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="valide">Validé</SelectItem>
                  <SelectItem value="refuse">Refusé</SelectItem>
                  <SelectItem value="a_retravailler">À retravailler</SelectItem>
                </SelectContent>
              </Select>
              {roleView !== 'juriste' && (
                <Input
                  placeholder="Email juriste (filtre)"
                  value={assignedToFilter}
                  onChange={(e) => setAssignedToFilter(e.target.value)}
                  className="w-72"
                  data-testid="juriste-filter-assigned-to"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Liste */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Documents ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 flex items-center justify-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-slate-400 italic" data-testid="juriste-queue-empty">
                Aucun document à valider.
              </p>
            ) : (
              <div className="space-y-2" data-testid="juriste-queue-list">
                {items.map((it) => {
                  const review = it.juridique_review || {};
                  const meta = STATUS_META[review.status] || STATUS_META.en_attente;
                  const Icon = meta.Icon;
                  return (
                    <div
                      key={it.document_id}
                      className="border border-slate-200 rounded p-3 hover:bg-slate-50 transition-colors"
                      data-testid={`juriste-queue-item-${it.document_id}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] ${meta.color}`}>
                              <Icon className="h-3 w-3 mr-1" /> {meta.label}
                            </Badge>
                            <span className="font-mono text-xs text-slate-700">{it.project_reference}</span>
                            <span className="text-[10px] text-slate-400">{it.document_id}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-1">
                            Assigné à <b>{review.assigned_to || '-'}</b> par <i>{review.assigned_by || '-'}</i>
                            {review.assigned_at && (
                              <span className="text-slate-400 ml-2">({review.assigned_at.slice(0, 16).replace('T', ' ')})</span>
                            )}
                          </p>
                          {review.decision_comment && (
                            <p className="text-xs text-slate-500 mt-1 italic">« {review.decision_comment} »</p>
                          )}
                        </div>
                        {review.status === 'en_attente' && (
                          <div className="flex items-center gap-1" data-testid={`juriste-actions-${it.document_id}`}>
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700"
                              onClick={() => { setDecisionModal({ item: it, decision: 'valide' }); setDecisionComment(''); }}
                              data-testid={`juriste-action-valide-${it.document_id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Valider
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-orange-50 border-orange-200 text-orange-700"
                              onClick={() => { setDecisionModal({ item: it, decision: 'a_retravailler' }); setDecisionComment(''); }}
                              data-testid={`juriste-action-retravailler-${it.document_id}`}>
                              <RotateCcw className="h-3 w-3 mr-1" /> À retravailler
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs bg-red-50 border-red-200 text-red-700"
                              onClick={() => { setDecisionModal({ item: it, decision: 'refuse' }); setDecisionComment(''); }}
                              data-testid={`juriste-action-refuse-${it.document_id}`}>
                              <XCircle className="h-3 w-3 mr-1" /> Refuser
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal decision */}
      <Dialog open={!!decisionModal} onOpenChange={(open) => { if (!open) { setDecisionModal(null); setDecisionComment(''); } }}>
        <DialogContent data-testid="juriste-decision-modal">
          <DialogHeader>
            <DialogTitle>Décision juriste — {decisionModal?.decision}</DialogTitle>
            <DialogDescription>
              Document <b>{decisionModal?.item.document_id}</b> • Projet {decisionModal?.item.project_reference}
              {['refuse', 'a_retravailler'].includes(decisionModal?.decision || '') && (
                <span className="block text-red-700 mt-1">Commentaire d&apos;au moins 10 caractères obligatoire.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={decisionComment}
            onChange={(e) => setDecisionComment(e.target.value)}
            placeholder={['refuse', 'a_retravailler'].includes(decisionModal?.decision || '') ? 'Justifier la décision (min 10 caractères)' : 'Commentaire (optionnel)'}
            data-testid="juriste-decision-comment"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDecisionModal(null); setDecisionComment(''); }} disabled={submittingDecision}>
              Annuler
            </Button>
            <Button onClick={submitDecision} disabled={submittingDecision} data-testid="juriste-decision-submit">
              {submittingDecision ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Enregistrer décision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
